import { useState, useCallback } from 'react';
import { POLISH_HOLIDAYS, MONTH_NAMES, DAY_NAMES, LEAVE_TYPES, STATUS_LABELS, isWeekend, isHoliday, isWorkDay, formatDate, toDateStr } from '../constants';

export default function Calendar({ leaves, onSelectRange, user }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selecting, setSelecting] = useState(false);
  const [selectStart, setSelectStart] = useState(null);
  const [selectEnd, setSelectEnd] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  // Build calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1);
  const lastDay = new Date(viewYear, viewMonth + 1, 0);
  // Monday-first grid
  let startDow = firstDay.getDay(); // 0=Sun
  startDow = startDow === 0 ? 6 : startDow - 1;

  const cells = [];
  for (let i = 0; i < startDow; i++) {
    const d = new Date(viewYear, viewMonth, 1 - (startDow - i));
    cells.push({ date: toDateStr(d), inMonth: false });
  }
  for (let d = 1; d <= lastDay.getDate(); d++) {
    cells.push({ date: toDateStr(new Date(viewYear, viewMonth, d)), inMonth: true });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ date: toDateStr(new Date(viewYear, viewMonth + 1, d)), inMonth: false });
  }

  // Leaves for display
  const leavesMap = {};
  for (const leave of leaves) {
    if (leave.status === 'rejected') continue;
    let cur = new Date(leave.date_from + 'T00:00:00');
    const end = new Date(leave.date_to + 'T00:00:00');
    while (cur <= end) {
      const ds = toDateStr(cur);
      if (!leavesMap[ds]) leavesMap[ds] = [];
      leavesMap[ds].push(leave);
      cur.setDate(cur.getDate() + 1);
    }
  }

  // Selection range
  const getSelectionRange = () => {
    if (!selectStart) return null;
    const end = hoveredDate || selectEnd;
    if (!end) return { from: selectStart, to: selectStart };
    return {
      from: selectStart < end ? selectStart : end,
      to: selectStart < end ? end : selectStart,
    };
  };

  const selRange = getSelectionRange();

  const isInSelection = (dateStr) => {
    if (!selRange) return false;
    return dateStr >= selRange.from && dateStr <= selRange.to;
  };

  const handleCellMouseDown = (dateStr, inMonth) => {
    if (!inMonth || !isWorkDay(dateStr)) return;
    setSelecting(true);
    setSelectStart(dateStr);
    setSelectEnd(null);
    setHoveredDate(dateStr);
  };

  const handleCellMouseEnter = (dateStr, inMonth) => {
    if (!inMonth) return;
    setHoveredDate(dateStr);
  };

  const handleCellMouseUp = (dateStr, inMonth) => {
    if (!inMonth || !selecting || !selectStart) return;
    setSelecting(false);
    setSelectEnd(dateStr);
    const from = selectStart < dateStr ? selectStart : dateStr;
    const to = selectStart < dateStr ? dateStr : selectStart;
    if (onSelectRange) onSelectRange(from, to);
    setSelectStart(null);
    setSelectEnd(null);
    setHoveredDate(null);
  };

  const todayStr = toDateStr(today);

  return (
    <div className="calendar-wrapper" onMouseLeave={() => { if (selecting) { setSelecting(false); setSelectStart(null); setHoveredDate(null); } }}>
      <div className="cal-header">
        <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
        <span className="cal-month-title">{MONTH_NAMES[viewMonth]} {viewYear}</span>
        <button className="cal-nav-btn" onClick={nextMonth}>›</button>
      </div>

      <div className="cal-grid">
        <div className="cal-days-header">
          {DAY_NAMES.map(d => <div key={d} className="cal-day-name">{d}</div>)}
        </div>

        <div className="cal-cells" onMouseUp={() => { if (selecting) setSelecting(false); }}>
          {cells.map(({ date, inMonth }) => {
            const weekend = isWeekend(date);
            const holiday = isHoliday(date);
            const isToday = date === todayStr;
            const inSel = isInSelection(date) && inMonth && isWorkDay(date);
            const dayLeaves = leavesMap[date] || [];

            let cellClass = 'cal-cell';
            if (!inMonth) cellClass += ' cal-cell-other';
            else if (holiday) cellClass += ' cal-cell-holiday';
            else if (weekend) cellClass += ' cal-cell-weekend';
            if (isToday) cellClass += ' cal-cell-today';
            if (inSel) cellClass += ' cal-cell-selecting';

            return (
              <div
                key={date}
                className={cellClass}
                onMouseDown={() => handleCellMouseDown(date, inMonth)}
                onMouseEnter={() => handleCellMouseEnter(date, inMonth)}
                onMouseUp={() => handleCellMouseUp(date, inMonth)}
                style={{ userSelect: 'none' }}
              >
                <div className="cal-day-num">{parseInt(date.slice(8))}</div>
                {holiday && inMonth && (
                  <div className="cal-holiday-label">{POLISH_HOLIDAYS[date]}</div>
                )}
                {dayLeaves.length > 0 && (
                  <div className="cal-leaves">
                    {dayLeaves.slice(0, 3).map((l, i) => {
                      const typeConf = LEAVE_TYPES[l.type] || LEAVE_TYPES.other;
                      const isOwn = l.user_id === user?.id;
                      return (
                        <div
                          key={i}
                          className="cal-leave-dot"
                          style={{
                            background: typeConf.bg,
                            color: typeConf.color,
                            border: `1px solid ${typeConf.color}40`,
                            opacity: l.status === 'pending' ? 0.6 : 1,
                          }}
                          title={`${l.user_name} — ${typeConf.label}${l.status === 'pending' ? ' (oczekuje)' : ''}`}
                        >
                          {user?.role === 'manager' ? l.user_name?.split(' ')[0] : (isOwn ? 'Ty' : l.user_name?.split(' ')[0])}
                          {l.status === 'pending' ? ' ?' : ''}
                        </div>
                      );
                    })}
                    {dayLeaves.length > 3 && (
                      <div className="cal-leave-dot" style={{ background: '#f0f0f0', color: '#666' }}>+{dayLeaves.length - 3}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="legend">
          <div className="legend-item"><div className="legend-dot" style={{ background: '#eaf4e6', border: '1px solid #2d6a1f' }}></div>Dziś</div>
          <div className="legend-item"><div className="legend-dot" style={{ background: '#fff8e6', border: '1px solid #e8a000' }}></div>Święto</div>
          <div className="legend-item"><div className="legend-dot" style={{ background: '#fafafa', border: '1px solid #ddd' }}></div>Weekend</div>
          {Object.entries(LEAVE_TYPES).map(([k, v]) => (
            <div key={k} className="legend-item"><div className="legend-dot" style={{ background: v.bg, border: `1px solid ${v.color}` }}></div>{v.label}</div>
          ))}
        </div>
        {onSelectRange && (
          <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>
            Kliknij i przeciągnij by zaznaczyć zakres urlopu
          </div>
        )}
      </div>
    </div>
  );
}
